import {
	Page, PageContent,
	PageGroup,
	PageGroupUpdate,
	PageInfo,
	SignInResponse,
	ThisUserInfo, UploadResponse,
	UploadsGCReport,
	UserInfo
} from "./dto";
import {currentPageIndex} from "./render";

export const AUTH_TOKEN_KEY = "AUTH_TOKEN";

export async function fetchPage(slug: string): Promise<Page> {
	const response = await apiCall(`/api/pages/by-slug/${slug}`);
	return await response.json();
}

export function setAuthToken(token: string) {
	localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function clearAuthToken() {
	localStorage.removeItem(AUTH_TOKEN_KEY);
}

export function hasAuthToken(): boolean {
	return localStorage.getItem(AUTH_TOKEN_KEY) !== null;
}

export function apiCall(url: string, init?: RequestInit) {
	const authToken = localStorage.getItem(AUTH_TOKEN_KEY);
	const req = init !== undefined ? Object.assign({}, init) : {};
	if (authToken !== null) {
		req.headers = req.headers !== undefined ? Object.assign({}, req.headers) : {};
		(req.headers as any)["Authorization"] = `Bearer ${authToken}`;
	}
	return fetch(url, req);
}

export async function callSignIn(login: string, password: string): Promise<SignInResponse | "invalid" | "unknown"> {
	const data = new URLSearchParams();
	data.append("login", login);
	data.append("password", password);
	const response = await fetch("/api/users/sign-in", {
		method: "post",
		body: data
	});
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else if (response.status === 401) {
		return "invalid";
	} else {
		return "unknown";
	}
}

export async function callSignOut(): Promise<void> {
	await fetch("/api/users/sign-out", {
		method: "post"
	});
}

export async function callCheckToken(): Promise<ThisUserInfo | "invalid" | "unknown"> {
	const response = await apiCall("/api/users/check", {
		method: "post"
	});
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else if (response.status >= 400 && response.status <= 499) {
		return "invalid";
	} else {
		return "unknown";
	}
}

export async function callChangePassword(
	oldPassword: string,
	newPassword: string
): Promise<"ok" | "invalid-old-password" | "unknown"> {
	const data = new URLSearchParams();
	data.append("oldPassword", oldPassword);
	data.append("newPassword", newPassword);
	const response = await apiCall("/api/users/password", {
		method: "put",
		body: data
	});
	if (response.status >= 200 && response.status <= 299) {
		return "ok";
	} else if (response.status >= 400 && response.status <= 499) {
		return "invalid-old-password";
	} else {
		return "unknown";
	}
}

export async function callUpdateProfile(
	email: string | null,
	displayName: string | null
): Promise<ThisUserInfo | "unknown"> {
	const data = new URLSearchParams();
	if (email !== null) {
		data.append("email", email);
	}
	if (displayName !== null) {
		data.append("displayName", displayName);
	}
	const response = await apiCall("/api/users/profile", {
		method: "put",
		body: data
	});
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else {
		return "unknown";
	}
}

function getPageIndexOrCurrent(index: number | null) {
	return index === null ? currentPageIndex() : index;
}

export async function callUpdatePageText(id: number, text: string, page: number | null): Promise<Page> {
	const response = await apiCall(
		`/api/pages/${id}?page=${getPageIndexOrCurrent(page)}`,
		{
			method: "put",
			body: text
		}
	);
	return await response.json();
}

export async function callUpdatePageProps(
	id: number,
	title: string,
	slug: string,
	lang: string | null,
	isGroup: boolean,
	showAuthor: boolean,
	showPublishedAt: boolean,
	page: number | null
): Promise<Page | "conflict" | "not-found" | "unknown"> {
	const data = new URLSearchParams();
	data.append("title", title);
	data.append("slug", slug);
	if (lang !== null) {
		data.append("lang", lang);
	}
	data.append("isGroup", isGroup ? "true" : "false");
	data.append("showAuthor", showAuthor ? "true" : "false");
	data.append("showPublishedAt", showPublishedAt ? "true" : "false");
	const response = await apiCall(`/api/pages/${id}/props?page=${getPageIndexOrCurrent(page)}`, {
		method: "put",
		body: data
	});
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else if (response.status === 404) {
		return "not-found";
	} else if (response.status === 409) {
		return "conflict";
	} else {
		return "unknown";
	}
}

export async function callCreatePage(
	title: string,
	slug: string,
	text: string,
	teaser: string | null,
	lang: string | null,
	isGroup: boolean,
	publish: boolean,
	showAuthor: boolean,
	showPublishedAt: boolean
): Promise<Page | "conflict" | "unknown"> {
	const data = new URLSearchParams();
	data.append("title", title);
	data.append("slug", slug);
	data.append("text", text);
	if (teaser !== null) {
		data.append("teaser", teaser);
	}
	if (lang !== null) {
		data.append("lang", lang);
	}
	data.append("isGroup", isGroup ? "true" : "false");
	data.append("publish", publish ? "true" : "false");
	data.append("showAuthor", showAuthor ? "true" : "false");
	data.append("showPublishedAt", showPublishedAt ? "true" : "false");
	const response = await apiCall(`/api/pages`, {
		method: "post",
		body: data
	});
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else if (response.status === 409) {
		return "conflict";
	} else {
		return "unknown";
	}
}

export async function callDeletePage(id: number): Promise<"ok" | "not-found" | "unknown"> {
	const response = await apiCall(`/api/pages/${id}`, {
		method: "delete"
	});
	if (response.status >= 200 && response.status <= 299) {
		return "ok";
	} else if (response.status === 404) {
		return "not-found";
	} else {
		return "unknown";
	}
}

export async function callListPages(): Promise<PageInfo[]> {
	const response = await apiCall("/api/pages/all");
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else {
		return [];
	}
}

export async function callPublishPage(id: number, page: number | null): Promise<Page | "not-found" | "unknown"> {
	const response = await apiCall(`/api/pages/${id}/publish?page=${getPageIndexOrCurrent(page)}`, {
		method: "post"
	});
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else if (response.status === 404) {
		return "not-found";
	} else {
		return "unknown";
	}
}

export async function callUnpublishPage(id: number, page: number | null): Promise<Page | "not-found" | "unknown"> {
	const response = await apiCall(`/api/pages/${id}/unpublish?page=${getPageIndexOrCurrent(page)}`, {
		method: "post"
	});
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else if (response.status === 404) {
		return "not-found";
	} else {
		return "unknown";
	}
}

export async function callListGroups(): Promise<PageGroup[]> {
	const response = await apiCall("/api/pages/groups");
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else {
		return [];
	}
}

export async function callSetPageGroups(
	pageId: number,
	groups: PageGroupUpdate[],
	page: number | null
): Promise<Page | "not-found" | "unknown"> {
	const response = await apiCall(
		`/api/pages/${pageId}/groups?page=${getPageIndexOrCurrent(page)}`,
		{
			method: "put",
			headers: {
				"Content-Type": "application/json"
			},
			body: JSON.stringify(groups)
		}
	);
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else if (response.status === 404) {
		return "not-found";
	} else {
		return "unknown";
	}
}

export async function callListUsers(): Promise<UserInfo[] | "forbidden" | "unknown"> {
	const response = await apiCall("/api/users/all");
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else if (response.status === 403) {
		return "forbidden";
	} else {
		return "unknown";
	}
}

export async function callSetIsAdmin(id: number): Promise<"ok" | "not-found" | "forbidden" | "unknown"> {
	const response = await apiCall(`/api/users/${id}/is-admin`, {
		method: "post"
	});
	if (response.status >= 200 && response.status <= 299) {
		return "ok";
	} else if (response.status === 403) {
		return "forbidden";
	} else if (response.status === 404) {
		return "not-found";
	} else {
		return "unknown";
	}
}

export async function callClearIsAdmin(id: number): Promise<"ok" | "not-found" | "forbidden" | "unknown"> {
	const response = await apiCall(`/api/users/${id}/is-admin`, {
		method: "delete"
	});
	if (response.status >= 200 && response.status <= 299) {
		return "ok";
	} else if (response.status === 403) {
		return "forbidden";
	} if (response.status === 404) {
		return "not-found";
	} else {
		return "unknown";
	}
}

export async function callCreateUser(
	login: string,
	password: string | null,
	isAdmin: boolean
): Promise<UserInfo | "forbidden" | "conflict" | "unknown"> {
	const data = new URLSearchParams();
	data.append("login", login);
	if (password !== null) {
		data.append("password", password);
	}
	data.append("isAdmin", isAdmin ? "true" : "false");
	const response = await apiCall(`/api/users`, {
		method: "post",
		body: data
	});
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else if (response.status === 403) {
		return "forbidden";
	} else if (response.status === 409) {
		return "conflict";
	} else {
		return "unknown";
	}
}

export async function callDeleteUser(id: number): Promise<"ok" | "forbidden" | "not-found" | "unknown"> {
	const response = await apiCall(`/api/users/${id}`, {
		method: "delete"
	});
	if (response.status >= 200 && response.status <= 299) {
		return "ok";
	} else if (response.status === 403) {
		return "forbidden";
	} else if (response.status === 404) {
		return "not-found";
	} else {
		return "unknown";
	}
}

export async function callSetUserPassword(
	id: number,
	password: string
): Promise<"ok" | "forbidden" | "not-found" | "unknown"> {
	const response = await apiCall(`/api/users/${id}/password`, {
		method: "put",
		body: password
	});
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else if (response.status === 403) {
		return "forbidden";
	} else if (response.status === 404) {
		return "not-found";
	} else {
		return "unknown";
	}
}

export async function callClearUserPassword(id: number): Promise<"ok" | "forbidden" | "not-found" | "unknown"> {
	const response = await apiCall(`/api/users/${id}/password`, {
		method: "delete"
	});
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else if (response.status === 403) {
		return "forbidden";
	} else if (response.status === 404) {
		return "not-found";
	} else {
		return "unknown";
	}
}

export async function callSetUserLogin(
	id: number,
	login: string
): Promise<"ok" | "forbidden" | "conflict" | "not-found" | "unknown"> {
	const response = await apiCall(`/api/users/${id}/login`, {
		method: "put",
		body: login
	});
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else if (response.status === 403) {
		return "forbidden";
	} else if (response.status === 409) {
		return "conflict";
	} else if (response.status === 404) {
		return "not-found";
	} else {
		return "unknown";
	}
}

export async function callListPageGroups(id: number): Promise<PageGroup[] | "not-found" | "unknown"> {
	const response = await apiCall(`/api/pages/${id}/groups`);
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else if (response.status === 404) {
		return "not-found";
	} else {
		return "unknown";
	}
}

export async function callGetContent(id: number): Promise<PageContent | "not-found" | "unknown"> {
	const response = await apiCall(`/api/pages/${id}/content`);
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else if (response.status === 404) {
		return "not-found";
	} else {
		return "unknown";
	}
}

export async function callSetTeaser(id: number, teaser: string): Promise<void> {
	await apiCall(`/api/pages/${id}/teaser`, {
		method: "put",
		body: teaser
	});
}

export async function callDeleteTeaser(id: number): Promise<void> {
	await apiCall(`/api/pages/${id}/teaser`, {
		method: "delete"
	});
}

export async function callUploadFile(file: Blob, filename?: string): Promise<UploadResponse | "unknown"> {
	const data = new FormData();
	data.append("upload", file, filename);
	const response = await apiCall(`/api/uploads`, {
		method: "post",
		body: data
	});
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else {
		return "unknown";
	}
}

export async function callUploadsGC(): Promise<UploadsGCReport | "unknown"> {
	const response = await apiCall(`/api/uploads/gc`, {
		method: "post"
	});
	if (response.status >= 200 && response.status <= 299) {
		return await response.json();
	} else {
		return "unknown";
	}
}
